// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { Trail } from 'presentations/views/components/atoms/Transition';
import Icon from 'presentations/views/components/atoms/Icon';
import Button from 'presentations/views/components/atoms/Button';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import Avatar, {
  Group as AvatarGroup,
} from 'presentations/views/components/atoms/Avatar';

import {
  borderRadius,
  fontSize,
  fontWeight,
  hideScrollbar,
  mediaMax,
  spacing,
  zIndex,
} from 'presentations/views/styles';
import {
  black,
  gray01,
  gray03,
  gray04,
  gray06,
  white,
} from 'presentations/views/styles/color';
import { TimeList } from 'presentations/api/helpers/event_type';
import { DateTime, getDateTime } from 'infras/datetime';
import { SharedGuestUserItem } from 'presentations/api/schema/user';
import {
  TimeListViewerCalendarEvent,
  UnavailableInfomation,
} from 'presentations/api/schema/calendar_event';

import { useTimeList } from '../../../shared/hooks/useTimeList';
import ViewMoreButton from '../../../shared/components/ViewMoreButton';
import { useMemo, useRef } from 'react';
import { eventTypeThemeColors } from 'domains/models/event_type/color';
import { relativeLuminanceW3C } from 'presentations/views/helpers/color';

type Props = {
  isLoading: boolean;
  isViewMoreLoading: boolean;
  timeZone: string | null;
  duration: number;
  calendarDateList: {
    date: string;
    timeList: TimeList;
  }[];
  color: string;
  isRevealGuestUser: boolean;
  isShowViewerCalendar: boolean;
  isHostDiagnosticMode: boolean;
  onSelect: (
    time: string,
    date: DateTime | undefined,
    users: SharedGuestUserItem[],
  ) => void;
  onClickBlockTime: (
    time: string,
    unavailableInformations: UnavailableInfomation[],
  ) => void;
  onClickEventName: (
    element: HTMLElement,
    event: TimeListViewerCalendarEvent,
  ) => void;
  onClickViewMore: () => void;
};

export default function Component(props: Props) {
  const isLightColor = useMemo(
    () =>
      !eventTypeThemeColors.includes(props.color) &&
      relativeLuminanceW3C(props.color) >= 0.5,
    [props.color],
  );

  return (
    <StyledContainer>
      <StyledListContent>
        {props.isLoading && <LoadingPresenter />}
        {props.calendarDateList.map((dateItem, i) => {
          return (
            <DatePresenter
              key={dateItem.date}
              isLightColor={isLightColor}
              date={dateItem.date}
              timeList={dateItem.timeList}
              timeZone={props.timeZone || 'Asia/Tokyo'}
              duration={props.duration}
              isRevealGuestUser={props.isRevealGuestUser}
              isShowViewerCalendar={props.isShowViewerCalendar}
              isHostDiagnosticMode={props.isHostDiagnosticMode}
              color={props.color}
              onSelect={props.onSelect}
              onClickBlockTime={props.onClickBlockTime}
              onClickEventName={props.onClickEventName}
            />
          );
        })}
        <StyledViewMoreButtonContainer>
          <ViewMoreButton
            isLoading={props.isViewMoreLoading}
            onClick={props.onClickViewMore}
          />
        </StyledViewMoreButtonContainer>
      </StyledListContent>
    </StyledContainer>
  );
}

const LoadingPresenter = () => {
  return (
    <>
      {new Array(14).fill(0).map((_, index) => (
        <StyledLoadingContainer key={`loading_${index}`}>
          <Skeleton width="100%" height="100%" />
        </StyledLoadingContainer>
      ))}
    </>
  );
};

const DatePresenter = (props: {
  date: string;
  isLightColor: boolean;
  timeZone: string;
  timeList: TimeList;
  duration: number;
  isRevealGuestUser: boolean;
  isShowViewerCalendar: boolean;
  isHostDiagnosticMode: boolean;
  color: string;
  onSelect: (
    time: string,
    date: DateTime | undefined,
    users: SharedGuestUserItem[],
  ) => void;
  onClickBlockTime: (
    time: string,
    unavailableInformations: UnavailableInfomation[],
  ) => void;
  onClickEventName: (
    element: HTMLElement,
    event: TimeListViewerCalendarEvent,
  ) => void;
}) => {
  const t = translateInComponent(translations);
  const dateText = getDateTime(props.date, props.timeZone).toFormat(
    t('date format'),
  );

  const buttonTextColor = props.isLightColor ? black : white;
  const buttonBackgroundColor = props.color;

  return (
    <StyledTrail isOpen>
      <StyledDateContainer>
        <StyledDateText>{dateText}</StyledDateText>
        <StyledTimeListContainer>
          {props.timeList.map((timeListItem, index) => {
            const {
              date,
              startTimeText,
              endTimeText,
              calendarEvent,
              guestUsers,
              hasUnavailableInfomation,
              unavailableInformations,
              isTransparentEvent,
              isCapacityFull,
              remainingCapacity,
            } = useTimeList({
              date: props.date,
              timeZone: props.timeZone,
              timeListItem,
              duration: props.duration,
            });
            const isDisabledButton =
              (!props.isHostDiagnosticMode && hasUnavailableInfomation) ||
              isCapacityFull;
            const buttonText =
              props.isHostDiagnosticMode && hasUnavailableInfomation
                ? t('block details')
                : t('select');

            return (
              <TimeRowPresenter
                key={index}
                isCapacityFull={isCapacityFull}
                isHostDiagnosticMode={props.isHostDiagnosticMode}
                isUnavailable={hasUnavailableInfomation}
              >
                <div>
                  <StyledTimeText>
                    {startTimeText}~{endTimeText}
                  </StyledTimeText>
                  <BlockReasonPresenter
                    isHostDiagnosticMode={props.isHostDiagnosticMode}
                    isTransparentEvent={isTransparentEvent}
                    eventName={calendarEvent?.name ?? ''}
                    dotColor={calendarEvent?.color ?? ''}
                    hasUnavailableInfomation={hasUnavailableInfomation}
                    unavailableInformations={unavailableInformations}
                    onClickEventName={(el) => {
                      calendarEvent &&
                        props.onClickEventName(el, calendarEvent);
                    }}
                  />
                  {props.isShowViewerCalendar && (
                    <EventNamePresenter
                      eventName={calendarEvent?.name ?? ''}
                      isTransparentEvent={isTransparentEvent}
                      dotColor={calendarEvent?.color ?? ''}
                      onClick={(el) => {
                        calendarEvent &&
                          props.onClickEventName(el, calendarEvent);
                      }}
                    />
                  )}
                  <CapacityPresenter remainingCapacity={remainingCapacity} />
                  <GuestUsersPresenter
                    isRevealGuestUser={props.isRevealGuestUser}
                    users={guestUsers}
                  />
                </div>
                <StyledSelectButton
                  color="primary"
                  textColor={buttonTextColor}
                  backgroundColor={buttonBackgroundColor}
                  isDisabled={isDisabledButton}
                  onClick={() => {
                    if (props.isHostDiagnosticMode) {
                      props.onClickBlockTime(
                        startTimeText,
                        unavailableInformations,
                      );
                    } else {
                      props.onSelect(startTimeText, date, guestUsers);
                    }
                  }}
                >
                  {buttonText}
                </StyledSelectButton>
              </TimeRowPresenter>
            );
          })}
        </StyledTimeListContainer>
      </StyledDateContainer>
    </StyledTrail>
  );
};

const TimeRowPresenter = (props: {
  isUnavailable: boolean;
  isHostDiagnosticMode: boolean;
  isCapacityFull: boolean;
  children: React.ReactNode;
}) => {
  if (props.isCapacityFull) {
    return (
      <StyledUnavailableTimeRow>{props.children}</StyledUnavailableTimeRow>
    );
  } else if (props.isUnavailable && props.isHostDiagnosticMode) {
    return (
      <StyledUnavailableTimeRow>{props.children}</StyledUnavailableTimeRow>
    );
  } else if (props.isUnavailable && !props.isHostDiagnosticMode) {
    return null;
  }

  return <StyledTimeRow>{props.children}</StyledTimeRow>;
};

const BlockReasonPresenter = (props: {
  isHostDiagnosticMode: boolean;
  isTransparentEvent: boolean;
  eventName: string;
  dotColor: string;
  hasUnavailableInfomation: boolean;
  unavailableInformations: UnavailableInfomation[];
  onClickEventName: (element: HTMLElement) => void;
}) => {
  const t = translateInComponent(translations);
  const eventNameRef = useRef(null);

  if (props.isHostDiagnosticMode && props.hasUnavailableInfomation) {
    return (
      <StyledBlockReasonContainer>
        <StyledBlockReasonIcon name="information-outline" color="primary" />
        {props.unavailableInformations.map((e) => (
          <StyledBlockReasonText key={e.reason}>
            {t(`block reason ${e.reason}`)}
          </StyledBlockReasonText>
        ))}
      </StyledBlockReasonContainer>
    );
  } else if (
    props.isHostDiagnosticMode &&
    props.eventName &&
    props.isTransparentEvent
  ) {
    return (
      <StyledBlockReasonContainer
        ref={eventNameRef}
        onClick={() => {
          if (eventNameRef.current != null) {
            props.onClickEventName(eventNameRef.current!);
          }
        }}
      >
        <StyledEventTextContainer>
          <StyledDot color={props.dotColor} />
          <StyledBlockReasonText>{props.eventName}</StyledBlockReasonText>
          <StyledNotExistSchedule>
            {t('no schedule setting')}
          </StyledNotExistSchedule>
        </StyledEventTextContainer>
      </StyledBlockReasonContainer>
    );
  }

  return null;
};

const EventNamePresenter = (props: {
  eventName: string | null;
  isTransparentEvent: boolean;
  dotColor: string;
  onClick: (element: HTMLElement) => void;
}) => {
  const eventNameRef = useRef(null);

  if (!props.isTransparentEvent && props.eventName) {
    return (
      <StyledEventTextContainer
        ref={eventNameRef}
        onClick={() => {
          if (eventNameRef.current != null) {
            props.onClick(eventNameRef.current!);
          }
        }}
      >
        <StyledDot color={props.dotColor} />
        <StyledEventName>{props.eventName}</StyledEventName>
      </StyledEventTextContainer>
    );
  }

  return null;
};

const GuestUsersPresenter = (props: {
  isRevealGuestUser: boolean;
  users: SharedGuestUserItem[];
}) => {
  if (props.isRevealGuestUser && props.users.length > 0) {
    return (
      <StyledAvatarGroup max={3} spacing={4}>
        {props.users.map((user) => (
          <StyledAvatar src={user.profileImage} color={user.color}>
            {user.name.charAt(0)}
          </StyledAvatar>
        ))}
      </StyledAvatarGroup>
    );
  }

  return null;
};

const CapacityPresenter = (props: { remainingCapacity: number | null }) => {
  const t = translateInComponent(translations);

  if (props.remainingCapacity === null) {
    return null;
  } else if (props.remainingCapacity === 0) {
    return (
      <StyledCapacityFullText>{t('full capacity')}</StyledCapacityFullText>
    );
  }

  return (
    <StyledCapacityText>
      {t('remaining capacity', {
        count: props.remainingCapacity,
      })}
    </StyledCapacityText>
  );
};

const StyledContainer = styled.div``;
const StyledListContent = styled.div`
  ${hideScrollbar};
`;
const StyledTimeRow = styled.div`
  padding: ${spacing(16)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(6)};
  font-weight: ${fontWeight('bold')};
  &:not(:nth-child(1)) {
    margin-top: ${spacing(16)};
  }
`;
const StyledUnavailableTimeRow = styled(StyledTimeRow)`
  background-color: ${gray06};
  color: ${gray01};
`;
const StyledLoadingContainer = styled.div`
  height: 72px;
  margin-top: ${spacing(16)};
`;
const StyledTrail = styled(Trail)``;
const StyledDateContainer = styled.div`
  margin-top: ${spacing(24)};
  ${mediaMax.desktop} {
    display: flex;
  }
  ${mediaMax.tablet} {
    display: block;
  }
`;
const StyledDateText = styled.div`
  width: 100%;
  text-align: left;
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
  line-height: 1.48;
  background-color: ${white};
  padding-bottom: ${spacing(16)};
  z-index: ${zIndex(1100)};

  ${mediaMax.desktop} {
    padding-top: ${spacing(24)};
    padding-bottom: 0;
    width: 100px;
    text-align: center;
  }
  ${mediaMax.tablet} {
    padding-top: 0;
    padding-bottom: ${spacing(16)};
    width: 100%;
    text-align: left;
  }
`;
const StyledTimeListContainer = styled.div`
  flex: 1;
  ${mediaMax.desktop} {
    margin-left: ${spacing(24)};
  }
  ${mediaMax.tablet} {
    margin-left: 0;
    margin-top: 0;
  }
`;
const StyledTimeText = styled.div`
  font-size: ${fontSize(16)};
  line-height: 1.48;
`;
const StyledSelectButton = styled(Button)<{
  textColor: (props: any) => string;
  backgroundColor: string;
}>`
  flex: 1;
  max-width: 160px;
  height: 40px;
  color: ${({ textColor }) => textColor};
  background-color: ${({ backgroundColor }) => backgroundColor};
  :hover {
    color: ${({ textColor }) => textColor};
    background-color: ${({ backgroundColor }) => backgroundColor};
  }
`;
const StyledEventTextContainer = styled.div`
  display: flex;
  align-items: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  /* cursor: pointer; */
`;
const StyledNotExistSchedule = styled.span`
  font-size: ${fontSize(12)};
  border-left: 1px solid ${gray03};
  padding-left: ${spacing(8)};
  margin-left: ${spacing(8)};
`;
const StyledEventName = styled.span`
  font-size: ${fontSize(12)};
  color: ${gray01};
`;
const StyledDot = styled.div<{ color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  margin-right: ${spacing(8)};
`;
const StyledBlockReasonContainer = styled.div`
  display: flex;
  align-items: center;
`;
const StyledBlockReasonIcon = styled(Icon)`
  width: 20px !important;
  height: 20px !important;
  margin-right: ${spacing(4)};
  flex-shrink: 0;

  ${mediaMax.smartphone} {
    width: 16px !important;
    height: 16px !important;
  }
`;
const StyledBlockReasonText = styled.div`
  font-size: ${fontSize(12)};
  padding-right: ${spacing(8)};
  border-right: 1px solid ${gray03};
  margin-right: ${spacing(8)};

  :last-of-type {
    padding-right: 0;
    border-right: none;
    margin-right: 0;
  }
`;
const StyledAvatarGroup = styled(AvatarGroup)`
  justify-content: flex-end;
  align-items: center;
  margin-top: ${spacing(8)};

  .MuiAvatar-colorDefault {
    width: 20px;
    height: 20px;
    font-size: ${fontSize(10)};
  }
`;
const StyledAvatar = styled(Avatar)<{ color: string }>`
  width: 20px;
  height: 20px;
  font-size: ${fontSize(12)};
  font-weight: initial;

  ${({ color }) => css`
    background-color: ${color};
  `}
`;
const StyledCapacityText = styled.span`
  font-size: ${fontSize(12)};
  font-weight: initial;
`;
const StyledCapacityFullText = styled(StyledCapacityText)`
  color: ${gray01};
`;
const StyledViewMoreButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: ${spacing(24)} auto;
`;
